import React from 'react';
import 'leaflet-editable';
import 'leaflet.path.drag';
import { FeatureGroup, MapContainer, Marker, Polyline, Popup } from 'react-leaflet';
import L from 'patches/leaflet';
import { EditControl } from 'react-leaflet-draw';
import MIcon from 'views/MIcon/MIcon';
import { MMapRoute, IMMapMarker, IMMapPolyline, MMapCustomControlButton } from './MMap.model';
import classes from './MMap.module.scss';
import { BiExpandAlt } from 'react-icons/bi';
import { FiMinus, FiPlus } from 'react-icons/fi';
import MTooltip from 'views/MTooltip/MTooltip';
import { MTooltipPosition } from 'views/MTooltip/MTooltip.model';
import { getCurrentDirection } from 'utils/helpers';
import { LanguageDirection } from 'utils/enums/language';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import { ControlMapPosition } from './MMap.utils';
import { ILatLng } from 'models/latlng';
import { FaRegTrashCan } from 'react-icons/fa6';

type Props<Clusters extends boolean> = {
  zoom: number;
  featureGroupRef: React.MutableRefObject<L.FeatureGroup<unknown> | null>;
  containerRef?: React.MutableRefObject<HTMLDivElement | null>;
  scrollWheelZoom?: boolean;
  className?: string;
  editable?: boolean;
  hideFullScreenControl?: boolean;
  hideZoomControls?: boolean;
  isMapFullScreen: boolean;
  markers?: IMMapMarker<Clusters>[];
  polylines?: IMMapPolyline[];
  routes?: MMapRoute[];
  style?: React.CSSProperties;
  clusters?: Clusters;
  center: [number, number];
  zoomControlClassName?: string;
  customControlButtons?: MMapCustomControlButton[];
  mapOverlayElement?: JSX.Element;
  polygons?: ILatLng[];
  onPolylineClick?: (id: string) => void;
  onCreate: (map: L.Map) => void;
  onZoomInClick: () => void;
  onZoomOutClick: () => void;
  onFullScreenClick: () => void;
  onCreatedPolygon: (e: L.DrawEvents.Created) => void;
  onEditedPolygon: (e: L.DrawEvents.Edited) => void;
  onDeletedPolygon: () => void;
  polylineToEdit?: L.LatLng[];
  onChangePolyline?: (coordinates?: L.LatLng[]) => void;
};

const MMapView = <Clusters extends boolean>(props: React.PropsWithChildren<Props<Clusters>>): JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className={classNames(classes['container'], props.className)} ref={props.containerRef}>
      <MapContainer
        style={{ ...props.style, zIndex: 0 }}
        whenCreated={props.onCreate}
        className={props.className}
        center={props.center}
        zoom={props.zoom}
        minZoom={1}
        maxZoom={18}
        zoomControl={false}
        scrollWheelZoom={props.scrollWheelZoom ?? true}
        editable={props.editable}
        attributionControl={false}
        maxBounds={[
          // Ensure that the map can't be zoomed or panned outside of the layer, vertically
          [90, -1800],
          [-90, 1800],
        ]}
      >
        {props.polygons ? (
          <FeatureGroup ref={props.featureGroupRef}>
            <EditControl
              position={ControlMapPosition.TopLeft}
              onCreated={props.onCreatedPolygon}
              onEdited={props.onEditedPolygon}
              draw={{
                // Hide the "Create Polygon" option if one already exists.
                polygon: !props.polygons?.length ? true : false,
                polyline: false,
                rectangle: false,
                circle: false,
                marker: false,
                circlemarker: false,
              }}
              edit={{
                remove: false,
                edit: {},
              }}
            />
            <button
              onClick={props.onDeletedPolygon}
              type="button"
              disabled={!props.polygons.length}
              className={classNames(classes['removePolygonBtn'], 'leaflet-draw-toolbar', 'leaflet-bar')}
            >
              <FaRegTrashCan />
            </button>
          </FeatureGroup>
        ) : null}

        {!props.clusters
          ? props.markers?.map((marker: IMMapMarker<Clusters>, index: number) => {
              return (
                <React.Fragment key={marker.id || index}>
                  <Marker
                    draggable={marker.draggable}
                    icon={marker.icon}
                    position={[marker.coord.lat, marker.coord.lng]}
                    eventHandlers={marker.eventHandlers}
                  >
                    {marker.popup ? <Popup>{marker.popup}</Popup> : null}
                  </Marker>
                </React.Fragment>
              );
            })
          : null}
        {props.polylines?.map((polyline: IMMapPolyline) => {
          // Start with any handlers the polyline itself provided
          const mergedHandlers: L.LeafletEventHandlerFnMap = { ...(polyline.eventHandlers || {}) };

          // If the parent supplied onPolylineClick, chain it AFTER any user click handler
          if (props.onPolylineClick) {
            // Keep any existing click handler (if present)
            const userClick = mergedHandlers.click as unknown as ((e: L.LeafletMouseEvent) => void) | undefined;

            // Install a properly typed click handler
            const chainedClick: (e: L.LeafletMouseEvent) => void = (e) => {
              if (typeof userClick === 'function') {
                userClick(e);
              }

              props.onPolylineClick!(polyline.id);
            };

            // Assign back with the exact type that Leaflet expects
            mergedHandlers.click = chainedClick as unknown as L.LeafletEventHandlerFnMap['click'];
          }

          return polyline.className === 'osrm-preview' ? (
            // Render ONLY the OSRM preview with proper style props
            <Polyline
              key={polyline.key}
              positions={polyline.positions}
              className={polyline.className}
              pathOptions={{
                color: polyline.color,
                weight: polyline.weight ?? 6,
                opacity: polyline.opacity ?? 1,
              }}
              eventHandlers={polyline.eventHandlers ?? { click: () => props.onPolylineClick?.(polyline.id) }}
            />
          ) : (
            // Leave your existing rendering untouched for everything else
            <Polyline
              key={polyline.key}
              weight={polyline.weight || 4}
              positions={polyline.positions}
              pathOptions={{
                ...(polyline?.color ? { dashArray: polyline?.color } : {}),
                ...(polyline?.dashArray ? { dashArray: polyline?.dashArray } : {}),
                ...(polyline?.className ? { dashArray: polyline?.className } : {}),
              }}
              color={polyline.color}
              opacity={polyline.opacity}
              eventHandlers={{ click: () => props.onPolylineClick?.(polyline.id) }}
            />
          );
        })}

        <div className={classNames(classes['controls'], props.zoomControlClassName)}>
          {!props.hideZoomControls && (
            <React.Fragment>
              <MTooltip
                text="Zoom In"
                position={getCurrentDirection() === LanguageDirection.RTL ? MTooltipPosition.Right : MTooltipPosition.Left}
              >
                <button className={classes['controlBtn']} type="button" onClick={props.onZoomInClick}>
                  <MIcon name={FiPlus} size="m" className={classes['controlBtn__icon']}></MIcon>
                </button>
              </MTooltip>
              <MTooltip
                text="Zoom Out"
                position={getCurrentDirection() === LanguageDirection.RTL ? MTooltipPosition.Right : MTooltipPosition.Left}
              >
                <button className={classes['controlBtn']} type="button" onClick={props.onZoomOutClick}>
                  <MIcon name={FiMinus} size="m" className={classes['controlBtn__icon']}></MIcon>
                </button>
              </MTooltip>
            </React.Fragment>
          )}
          {!props.hideFullScreenControl && (
            <MTooltip
              text="Full-Screen"
              position={getCurrentDirection() === LanguageDirection.RTL ? MTooltipPosition.Right : MTooltipPosition.Left}
            >
              <button className={classes['controlBtn']} type="button" onClick={props.onFullScreenClick}>
                <MIcon name={BiExpandAlt} size="m" className={classes['controlBtn__icon']}></MIcon>
              </button>
            </MTooltip>
          )}
          {props.customControlButtons?.map((button: MMapCustomControlButton) => {
            return (
              <MTooltip
                key={`${button.id}_${button.className}`}
                text={button.name}
                position={getCurrentDirection() === LanguageDirection.RTL ? MTooltipPosition.Right : MTooltipPosition.Left}
              >
                <button className={classNames(classes['controlBtn'], button.className)} type="button" onClick={button.onClick}>
                  <MIcon name={button.icon} size="m" className={classes['controlBtn__icon']}></MIcon>
                </button>
              </MTooltip>
            );
          })}
        </div>
        {/* This element will only appear in full screen mode (it behaves differently in full screen, which we want to prevent in normal view) */}
        {props.isMapFullScreen ? props.mapOverlayElement : null}
      </MapContainer>
      {props.mapOverlayElement}
      <div className={classNames(classes['controls'], props.zoomControlClassName)}>
        {!props.hideZoomControls && (
          <React.Fragment>
            <MTooltip
              text={t('common.map.zoomIn')}
              position={getCurrentDirection() === LanguageDirection.RTL ? MTooltipPosition.Right : MTooltipPosition.Left}
            >
              <button className={classes['controlBtn']} type="button" onClick={props.onZoomInClick}>
                <MIcon name={FiPlus} size="m" className={classes['controlBtn__icon']}></MIcon>
              </button>
            </MTooltip>
            <MTooltip
              text={t('common.map.zoomOut')}
              position={getCurrentDirection() === LanguageDirection.RTL ? MTooltipPosition.Right : MTooltipPosition.Left}
            >
              <button className={classes['controlBtn']} type="button" onClick={props.onZoomOutClick}>
                <MIcon name={FiMinus} size="m" className={classes['controlBtn__icon']}></MIcon>
              </button>
            </MTooltip>
          </React.Fragment>
        )}
        {!props.hideFullScreenControl && (
          <MTooltip
            text={t('common.map.fullScreen')}
            position={getCurrentDirection() === LanguageDirection.RTL ? MTooltipPosition.Right : MTooltipPosition.Left}
          >
            <button className={classes['controlBtn']} type="button" onClick={props.onFullScreenClick}>
              <MIcon name={BiExpandAlt} size="m" className={classes['controlBtn__icon']}></MIcon>
            </button>
          </MTooltip>
        )}
        {props.customControlButtons?.map((button: MMapCustomControlButton) => {
          return (
            <MTooltip
              key={`${button.id}_${button.className}`}
              text={button.name}
              position={getCurrentDirection() === LanguageDirection.RTL ? MTooltipPosition.Right : MTooltipPosition.Left}
            >
              <button className={classNames(classes['controlBtn'], button.className)} type="button" onClick={button.onClick}>
                <MIcon name={button.icon} size="m" className={classes['controlBtn__icon']}></MIcon>
              </button>
            </MTooltip>
          );
        })}
      </div>
    </div>
  );
};

MMapView.displayName = 'MMapView';
MMapView.defaultProps = {};

export default MMapView;
